<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesDeliveryEmailEditorCopies from './SalesDeliveryEmailEditorCopies.vue';
import SalesDeliveryEmailEditorRecipients from './SalesDeliveryEmailEditorRecipients.vue';
import SalesDeliveryEmailEditorTabs from './SalesDeliveryEmailEditorTabs.vue';
import {
  isEditableRecipient,
  normalizeDeliveryCopies,
} from '../composables/salesDeliveryRecipients';

const props = defineProps({
  show: { type: Boolean, default: false },
  participants: { type: Array, default: () => [] },
  copies: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();
const tab = ref('vendedor');
const participantEmails = ref({});
const draftCopies = ref(normalizeDeliveryCopies([]));
const errors = ref({});

const editableParticipants = computed(() =>
  props.participants.filter(isEditableRecipient)
);

const visibleParticipants = computed(() =>
  editableParticipants.value.filter(
    participant => (participant.role || participant.party_role) === tab.value
  )
);

const reset = () => {
  participantEmails.value = editableParticipants.value.reduce(
    (result, participant) => ({
      ...result,
      [participant.id]: participant.email || '',
    }),
    {}
  );
  draftCopies.value = normalizeDeliveryCopies(props.copies);
  errors.value = {};
};

watch(
  () => [props.show, props.participants, props.copies],
  () => {
    if (props.show) reset();
  },
  { deep: true, immediate: true }
);

const updateParticipantEmail = (id, value) => {
  participantEmails.value = { ...participantEmails.value, [id]: value };
  delete errors.value[`participant_${id}`];
};

const updateCopy = (index, field, value) => {
  draftCopies.value = draftCopies.value.map((copy, copyIndex) =>
    copyIndex === index ? { ...copy, [field]: value } : copy
  );
  delete errors.value[`copy_${index}`];
};

const validate = () => {
  const nextErrors = {};
  editableParticipants.value.forEach(participant => {
    const email = participantEmails.value[participant.id] || '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors[`participant_${participant.id}`] = t(
        'SALES.DELIVERY.EMAILS.EDITOR.INVALID_EMAIL'
      );
    }
  });
  draftCopies.value.forEach((copy, index) => {
    if (copy.name || copy.email) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(copy.email || '')) {
        nextErrors[`copy_${index}`] = t(
          'SALES.DELIVERY.EMAILS.EDITOR.INVALID_EMAIL'
        );
      }
    }
  });
  errors.value = nextErrors;
  return Object.keys(nextErrors).length === 0;
};

const save = () => {
  if (!validate()) return;
  emit('save', {
    participants: editableParticipants.value.map(participant => ({
      id: participant.id,
      email: participantEmails.value[participant.id],
    })),
    copies: draftCopies.value.filter(copy => copy.name || copy.email),
  });
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="t('SALES.DELIVERY.EMAILS.EDITOR.TITLE')"
    max-width-class="max-w-[900px]"
    @close="emit('close')"
  >
    <div class="grid gap-4">
      <p class="text-sm text-lais-text-muted">
        {{ t('SALES.DELIVERY.EMAILS.EDITOR.DESCRIPTION') }}
      </p>
      <SalesDeliveryEmailEditorTabs
        v-model="tab"
        :participants="participants"
      />
      <SalesDeliveryEmailEditorRecipients
        v-if="tab !== 'copies'"
        :participants="visibleParticipants"
        :emails="participantEmails"
        :errors="errors"
        :disabled="disabled || saving"
        @update="updateParticipantEmail"
      />
      <SalesDeliveryEmailEditorCopies
        v-else
        :copies="draftCopies"
        :errors="errors"
        :disabled="disabled || saving"
        @update="updateCopy"
      />
    </div>
    <template #footer>
      <div class="flex flex-wrap justify-end gap-2">
        <LaisPrimaryButton
          variant="ghost"
          :disabled="saving"
          @click="emit('close')"
        >
          {{ t('SALES.DELIVERY.EMAILS.EDITOR.BACK') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          :is-loading="saving"
          :disabled="disabled"
          @click="save"
        >
          {{ t('SALES.DELIVERY.EMAILS.EDITOR.SAVE') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
